import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    cancel: string;
    continue: string;
    planIncludedStorage: (formattedIncludedStorage: string, storageType: string) => string;
    increaseStorage: string;
    decreaseStorage: string;
    extraStorageAdding: ({ formattedFileSize, formattedPrice, period, formattedIncrement, storageType }: {
        formattedFileSize: string;
        formattedPrice: string;
        period: string;
        formattedIncrement: string;
        storageType: string;
    }) => string;
    extraStorageRemoving: ({ formattedFileSize, formattedPrice, period, formattedIncrement, storageType }: {
        formattedFileSize: string;
        formattedPrice: string;
        period: string;
        formattedIncrement: string;
        storageType: string;
    }) => string;
    planCostIncrease: (costChange: number, period: string) => string;
    planCostDecrease: (costChange: number, period: string) => string;
    currentStorage: (formattedExtraStorage: string, storageType: string) => string;
    currentPerPrice: (formattedPrice: string, period: string, formattedIncrement: string) => string;
    actionDescriptionAdded: (formattedFileSize: string, storageType: string) => string;
    actionDescriptionRemoved: (formattedFileSize: string, storageType: string) => string;
    loadingLabel: string;
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            /** Button text to cancel storage management */
            cancel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ManageStorage.cancel?c=2ojhd8rzicmo0", defaultMessage: "Cancel" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Button text to continue with storage changes */
            continue: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ManageStorage.continue?c=lmf4jegcl01i", defaultMessage: "Continue" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text describing included storage in plan */
            planIncludedStorage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (formattedIncludedStorage: string, storageType: string): string => __intl__.intl().formatMessage({ id: "ManageStorage.planIncludedStorage?c=mmum26ckp68u", defaultMessage: "Your plan includes {formattedIncludedStorage} of {storageType}." }, { formattedIncludedStorage: formattedIncludedStorage, storageType: storageType }));
                },
                enumerable: true,
                configurable: true
            },
            /** Accessibility label for increase storage button */
            increaseStorage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ManageStorage.increaseStorage?c=3pkamgw08ucda", defaultMessage: "Increase storage" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Accessibility label for decrease storage button */
            decreaseStorage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ManageStorage.decreaseStorage?c=2g4bx4p91gvbb", defaultMessage: "Decrease storage" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text for adding extra storage */
            extraStorageAdding: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ formattedFileSize, formattedPrice, period, formattedIncrement, storageType }: {
                        formattedFileSize: string;
                        formattedPrice: string;
                        period: string;
                        formattedIncrement: string;
                        storageType: string;
                    }): string => __intl__.intl().formatMessage({ id: "ManageStorage.extraStorageAdding?c=35ajutus4xrg9", defaultMessage: "Adding {formattedFileSize} extra {storageType} ({formattedPrice} /{period} per {formattedIncrement})" }, { formattedFileSize: formattedFileSize, storageType: storageType, formattedPrice: formattedPrice, period: period, formattedIncrement: formattedIncrement }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text for removing extra storage */
            extraStorageRemoving: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ formattedFileSize, formattedPrice, period, formattedIncrement, storageType }: {
                        formattedFileSize: string;
                        formattedPrice: string;
                        period: string;
                        formattedIncrement: string;
                        storageType: string;
                    }): string => __intl__.intl().formatMessage({ id: "ManageStorage.extraStorageRemoving?c=h31rv6mtwtpl", defaultMessage: "Removing {formattedFileSize} extra {storageType} ({formattedPrice} /{period} per {formattedIncrement})" }, { formattedFileSize: formattedFileSize, storageType: storageType, formattedPrice: formattedPrice, period: period, formattedIncrement: formattedIncrement }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text describing plan cost increase */
            planCostIncrease: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (costChange: number, period: string): string => __intl__.intl().formatMessage({ id: "ManageStorage.planCostIncrease?c=37ezh4bbvbh1a", defaultMessage: "Your plan cost will increase by ${costChange} /{period} (plus tax)" }, { costChange: costChange, period: period }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text describing plan cost decrease */
            planCostDecrease: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (costChange: number, period: string): string => __intl__.intl().formatMessage({ id: "ManageStorage.planCostDecrease?c=1t8ppcspmfc9", defaultMessage: "Your plan cost will decrease by ${costChange} /{period} (plus tax)" }, { costChange: costChange, period: period }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text describing current extra storage amount */
            currentStorage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (formattedExtraStorage: string, storageType: string): string => __intl__.intl().formatMessage({ id: "ManageStorage.currentStorage?c=2opjehb4edkz1", defaultMessage: "You have {formattedExtraStorage} of extra {storageType}" }, { formattedExtraStorage: formattedExtraStorage, storageType: storageType }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text describing current storage price per unit */
            currentPerPrice: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (formattedPrice: string, period: string, formattedIncrement: string): string => __intl__.intl().formatMessage({ id: "ManageStorage.currentPerPrice?c=uli1c8xsbv8j", defaultMessage: "{formattedPrice} /{period} per {formattedIncrement} (plus tax)" }, { formattedPrice: formattedPrice, period: period, formattedIncrement: formattedIncrement }));
                },
                enumerable: true,
                configurable: true
            },
            /** Toast description for storage added */
            actionDescriptionAdded: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (formattedFileSize: string, storageType: string): string => __intl__.intl().formatMessage({ id: "ManageStorage.actionDescriptionAdded?c=3kmesguoxeloo", defaultMessage: "{formattedFileSize} of {storageType} was added to your plan." }, { formattedFileSize: formattedFileSize, storageType: storageType }));
                },
                enumerable: true,
                configurable: true
            },
            /** Toast description for storage removed */
            actionDescriptionRemoved: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (formattedFileSize: string, storageType: string): string => __intl__.intl().formatMessage({ id: "ManageStorage.actionDescriptionRemoved?c=1pwg79imienzu", defaultMessage: "{formattedFileSize} of {storageType} was removed from your plan." }, { formattedFileSize: formattedFileSize, storageType: storageType }));
                },
                enumerable: true,
                configurable: true
            },
            /** Loading label for content */
            loadingLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ManageStorage.loadingLabel?c=2z6x46esp49fs", defaultMessage: "Loading content..." }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
